/**
 * Maps raw telemetry metric names (snake_case) to node data field names (camelCase).
 * Shared between VerticalSLD (node updates) and SLDTab (detail panel live updates).
 */
export function mapMetricsToNodeData(
  deviceData: Record<string, number | string | boolean | null>,
  base: Record<string, unknown> = {},
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };

  for (const [metric, value] of Object.entries(deviceData)) {
    // RESERVED: keys attached synthetically by VerticalSLD.tsx's `liveDevices`
    // merge step (`_deviceType` at line ~110 and `deviceState` at line ~143)
    // are NOT in this switch and survive untouched via the `{ ...base }` spread
    // above. Do not add cases for them, and do not introduce real telemetry
    // metric names that would collide — underscore-prefixed metric names are
    // reserved for VerticalSLD-internal merge state across all callers of this
    // mapper.
    switch (metric) {
      // Common
      case 'active_power_kw': out.activePowerKw = value; break;
      case 'voltage_v': out.voltageV = value; break;
      case 'current_a': out.currentA = value; break;
      case 'reactive_power_kvar': out.reactivePowerKvar = value; break;
      case 'status': out.status = value; break;
      case 'state': out.state = value; break;
      case 'mode': out.mode = value; break;
      case 'faulted': out.faulted = value; break;
      case 'block_status': out.blockStatus = value; out.state = value; break;
      case 'block_mode': out.blockMode = value; out.mode = value; break;
      case 'comms_ok': out.commsOk = value; break;
      case 'last_seen_at': out.lastSeenAt = value; break;

      // BESS
      case 'state_of_charge': out.stateOfCharge = value; break;
      case 'battery_temperature': out.batteryTemperature = value; break;
      case 'cell_voltage_min': out.cellVoltageMin = value; break;
      case 'cell_voltage_max': out.cellVoltageMax = value; break;
      case 'state_of_health': out.stateOfHealth = value; break;
      case 'cycle_count': out.cycleCount = value; break;
      case 'max_power_kw': out.maxPowerKw = value; break;
      case 'min_power_kw': out.minPowerKw = value; break;
      case 'pack_voltage': out.packVoltageV = value; break;
      case 'pack_current': out.packCurrentA = value; break;
      case 'max_charge_power': out.maxChargeKw = value; break;
      case 'max_discharge_power': out.maxDischargeKw = value; break;
      case 'cell_temp_min': out.cellTempMin = value; break;
      case 'cell_temp_max': out.cellTempMax = value; break;
      case 'rack_count_total': out.racksTotal = value; break;
      case 'rack_count_online': out.racksOnline = value; break;
      case 'dc_bus_voltage': out.dcBusVoltageV = value; break;
      case 'dc_bus_current': out.dcBusCurrentA = value; break;
      case 'pcs_ac_frequency': out.pcsAcFrequencyHz = value; break;
      case 'pcs_ac_voltage': out.pcsAcVoltageV = value; break;

      // Generator
      case 'generator_power_kw': out.generatorPowerKw = value; break;
      case 'fuel_level': out.fuelLevelPct = value; break;
      case 'engine_speed': out.engineSpeedRpm = value; break;
      case 'engine_load': out.engineLoadPct = value; break;
      case 'run_hours': out.runHours = value; break;
      case 'coolant_temperature': out.coolantTempF = value; break;
      case 'oil_pressure': out.oilPressurePsi = value; break;
      case 'cumulative_energy': out.cumulativeEnergyKwh = value; break;
      case 'battery_voltage': out.batteryVoltageV = value; break;
      case 'avg_line_voltage': out.avgLineVoltageV = value; break;
      case 'avg_current': out.avgCurrentA = value; break;
      case 'slip_frequency': out.slipFrequencyHz = value; break;
      case 'sync_voltage_diff': out.syncVoltageDiffV = value; break;
      case 'apparent_power_kva': out.apparentPowerKva = value; break;
      case 'breaker_closed': out.breakerClosed = value; break;
      case 'estop_latched': out.estopLatched = value; break;

      // Meter
      case 'grid_power_kw': out.activePowerKw = value; break;
      case 'grid_reactive_kvar': out.reactivePowerKvar = value; break;
      case 'grid_voltage_v': out.voltageV = value; break;
      case 'voltage_phase_a': out.voltagePhaseA = value; break;
      case 'voltage_phase_b': out.voltagePhaseB = value; break;
      case 'voltage_phase_c': out.voltagePhaseC = value; break;
      case 'current_phase_a': out.currentPhaseA = value; break;
      case 'current_phase_b': out.currentPhaseB = value; break;
      case 'current_phase_c': out.currentPhaseC = value; break;
      case 'reactive_power': out.reactivePower = value; break;
      case 'apparent_power': out.apparentPower = value; break;
      case 'power_factor': out.powerFactor = value; break;
      case 'meter_frequency': out.frequencyHz = value; break;

      // EV Charger
      case 'active_sessions': out.activeSessions = value; break;
      case 'ports_available': out.portsAvailable = value; break;
    }
  }

  return out;
}
